import React, { useState, useEffect } from 'react';
import { UserSession, UserRole } from '../types';
import { Crown, ShieldCheck, Check, ChevronDown, LogOut } from 'lucide-react';
import { playPulseSeal } from '../utils/pulseAudio';
import { getRoleBadgeClasses } from '../utils/roleAccent';

interface MobileAccountRoleSectionProps {
  user: UserSession;
  onSwitchRole?: (role: UserRole) => void;
  onLogout?: () => void;
  onAfterAction?: () => void;
}

export const MobileAccountRoleSection: React.FC<MobileAccountRoleSectionProps> = ({
  user,
  onSwitchRole,
  onLogout,
  onAfterAction
}) => {
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [credentialTag, setCredentialTag] = useState<string | null>(
    user.providerCredential || null
  );

  // Derive credential tag automatically from user's canonical provider profile
  useEffect(() => {
    if (user.providerCredential) {
      setCredentialTag(user.providerCredential);
      return;
    }

    let isMounted = true;
    const fetchLinkedProviderCredential = async () => {
      try {
        const res = await fetch('/api/providers', { credentials: 'include' });
        if (!res.ok) return;
        const providers: any[] = await res.json();
        if (!isMounted || !Array.isArray(providers)) return;

        const isBillingLogin = (user.email || '').toLowerCase() === 'billing@allegianttherapy.com';
        const matchedProvider = isBillingLogin ? undefined : providers.find(p =>
          (user.providerId && p.id === user.providerId) ||
          (user.id && p.user_id === user.id) ||
          (user.email && p.email?.toLowerCase() === user.email.toLowerCase()) ||
          (user.name && p.name?.toLowerCase().includes(user.name.toLowerCase()))
        );

        if (!matchedProvider) {
          if (isMounted) setCredentialTag(null);
          return;
        }

        const role = (matchedProvider.role || '').toUpperCase();
        const title = (matchedProvider.title || '').toLowerCase();

        if (role === 'SLPA' || role === 'ASSISTANT' || title.includes('pathology assistant') || title.includes('slpa')) {
          if (isMounted) setCredentialTag('SLPA');
        } else if (role === 'SLP' || role === 'SUPERVISOR' || title.includes('speech-language pathologist') || title.includes('slp')) {
          if (isMounted) setCredentialTag('SLP');
        } else {
          if (isMounted) setCredentialTag(null);
        }
      } catch {
        if (isMounted) setCredentialTag(null);
      }
    };

    fetchLinkedProviderCredential();
    return () => {
      isMounted = false;
    };
  }, [user.providerCredential, user.providerId, user.id, user.email, user.name]);

  const assignedRoles = (user.assignedRoles && user.assignedRoles.length > 0)
    ? user.assignedRoles.filter((r) => ['OWNER', 'REFERRAL_COORDINATOR', 'SUPERVISOR', 'ASSISTANT'].includes(r))
    : [user.role];
  const isMultiRole = assignedRoles.length > 1;

  // Derive active clinical credential tag (CCC-SLP for licensed SLP, SLPA for ASSISTANT, or canonical linked provider)
  const effectiveCredentialTag = (() => {
    let rawTag: string | null = null;
    if (user.providerCredential) {
      rawTag = user.providerCredential;
    } else if (credentialTag) {
      rawTag = credentialTag;
    } else {
      const cleanEmail = (user.email || '').toLowerCase();
      if (cleanEmail === 'credentialing@allegianttherapy.com' || cleanEmail === 'justinefren1985@gmail.com') {
        rawTag = 'SLPA';
      } else if (user.role === 'SUPERVISOR') {
        rawTag = 'SLP';
      } else if (user.role === 'ASSISTANT') {
        rawTag = 'SLPA';
      }
    }

    if (!rawTag) return null;
    const clean = rawTag.trim();
    if (clean === 'SLP' || clean === 'CCC-SLP' || clean === 'MS, CCC-SLP') {
      return 'CCC-SLP';
    }
    if (clean === 'SLPA') {
      return 'SLPA';
    }
    return clean;
  })();

  const formatRoleLabel = (role: string) => {
    switch (role) {
      case 'OWNER':
      case 'ADMIN':
        return 'OWNER';
      case 'REFERRAL_COORDINATOR':
      case 'OFFICE':
        return 'REFERRAL COORDINATOR';
      case 'SUPERVISOR':
        return 'SUPERVISOR';
      case 'ASSISTANT':
        return 'ASSISTANT';
      default:
        return role;
    }
  };

  const handleRoleSelect = (targetRole: UserRole) => {
    if (onSwitchRole && user.role !== targetRole) {
      onSwitchRole(targetRole);
    }
    setIsRoleDropdownOpen(false);
    if (onAfterAction) {
      onAfterAction();
    }
  };

  const handleLogoutClick = () => {
    playPulseSeal({ cooldownMs: 120, volume: 0.35 });
    if (onAfterAction) {
      onAfterAction();
    }
    if (onLogout) {
      onLogout();
    }
  };

  const isSupervisor = user.role === 'SUPERVISOR';
  const isAssistant = user.role === 'ASSISTANT';
  const isClinicalMobile = isSupervisor || isAssistant;

  return (
    <div className="bg-white border border-slate-200/90 rounded-xl p-3 space-y-2.5 select-none shadow-xs">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-500">
          Account &amp; Role
        </span>
        {effectiveCredentialTag && (
          <span className="text-[9px] font-mono font-semibold px-2 py-0.5 rounded-full tracking-wider uppercase select-none bg-slate-100 text-slate-700 border border-slate-200">
            {effectiveCredentialTag}
          </span>
        )}
      </div>

      {/* User Info & Current Role Row */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold truncate text-slate-900">
            {user.name}
          </div>
          <div className="text-[10.5px] font-mono truncate text-slate-500">
            {user.email}
          </div>
        </div>

        {/* Current Active Role Badge */}
        <div className="shrink-0 flex items-center">
          <span className="text-[9.5px] font-mono font-bold px-2 py-0.8 rounded bg-slate-100 text-slate-800 border border-slate-200/90 tracking-wide uppercase flex items-center gap-1.5 select-none shadow-xs">
            <ShieldCheck className="w-3 h-3 text-slate-600 shrink-0" />
            <span>{user.role === 'ASSISTANT' ? 'SLPA' : formatRoleLabel(user.role)}</span>
          </span>
        </div>
      </div>

      {/* Role Switcher if multi-role authorized */}
      {isMultiRole && (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
            className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors cursor-pointer bg-slate-50 border border-slate-200 text-slate-700 hover:text-slate-900 hover:bg-slate-100 shadow-xs"
          >
            <span className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-500">Switch Role:</span>
              <span className="font-semibold text-slate-900">{formatRoleLabel(user.role)}</span>
            </span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 text-slate-500 ${isRoleDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {isRoleDropdownOpen && (
            <div
              className="mt-1.5 rounded-lg p-1 space-y-1 shadow-lg animate-in fade-in duration-100 bg-white border border-slate-200"
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {assignedRoles.map((r) => {
                const isActive = user.role === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRoleSelect(r as UserRole);
                    }}
                    className={`w-full px-2.5 py-1.5 rounded-md text-left text-xs flex items-center justify-between transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-slate-100 text-slate-950 font-semibold border-l-2 border-l-slate-900'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <ShieldCheck className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <span className="font-mono text-[11px]">{formatRoleLabel(r)}</span>
                    </div>
                    {isActive && (
                      <Check className="w-3.5 h-3.5 text-slate-900" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Sign Out Action Row */}
      {onLogout && (
        <div className="pt-0.5 border-t border-slate-100">
          <button
            type="button"
            onClick={handleLogoutClick}
            className="w-full flex items-center justify-center space-x-2 py-1.5 px-3 rounded-lg transition-all text-xs font-medium cursor-pointer bg-slate-50 hover:bg-rose-50 text-slate-600 hover:text-rose-700 border border-slate-200/90 hover:border-rose-200"
          >
            <LogOut className="w-3.5 h-3.5 text-slate-400 group-hover:text-rose-600" />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};
